/**
 * @fileoverview Module configuration utilities.
 * Provides helper functions for validating tab settings and processing
 * configuration change objects in module settings.
 * @module helperMethods/modules/index
 */

import _ from "lodash";

/**
 * Represents a tab configuration object.
 */
interface TabObject {
  title: string;
  url: string;
  style?: any;
}

/**
 * Validates that all tabs have required title and url fields.
 * Empty tab arrays are considered valid.
 *
 * @param {TabObject[]} tabs - Array of tab configuration objects
 * @returns {boolean} True if all tabs are valid or array is empty, false otherwise
 *
 * @example
 * const valid = tabSettingValidation([
 *   { title: 'Home', url: '/home' },
 *   { title: 'About', url: '/about' }
 * ]);
 * // Returns: true
 *
 * @example
 * const invalid = tabSettingValidation([
 *   { title: 'Home', url: '' }
 * ]);
 * // Returns: false
 */
export function tabSettingValidation(tabs: TabObject[]) {
  if (_.isEmpty(tabs)) {
    return true;
  }
  const validatedTabs: boolean[] = _.map(tabs, (tab: TabObject) => {
    return !!(tab.title && tab.url);
  });
  if (validatedTabs.includes(false)) {
    return false;
  } else {
    return true;
  }
}

/**
 * Recursively processes a configuration change object to extract latest values.
 * For arrays, takes the last element. For objects, recurses. Removes non-array/object properties.
 * Modifies the object in place.
 *
 * @param {any} obj - Configuration change object to process
 * @returns {void} Modifies obj in place
 *
 * @example
 * const changes = {
 *   setting1: ['old', 'new'],
 *   nested: { setting2: ['a', 'b', 'c'] }
 * };
 * mapChanges(changes);
 * // changes is now: { setting1: 'new', nested: { setting2: 'c' } }
 */
export function mapChanges(obj: any) {
  _.forEach(obj, (val, key) => {
    if (Array.isArray(val)) {
      obj[key] = _.last(val);
    } else if (typeof val === "object") {
      mapChanges(obj[key]);
    } else {
      delete obj[key];
    }
  });
}
